import {Check} from '@mui/icons-material'
import {Box, Typography, List, ListItem, ListItemButton} from '@mui/material'
import React, {useCallback} from 'react'

import {LoadSpacingConfiguration} from '@hconnect/tuc-processes/fields/loadSpacing/types'
import {useLoadSpacing} from '@hconnect/tuc-processes/fields/loadSpacing/useLoadSpacing'

interface TimeOnJobSiteSelectorProps {
  loadSpacingConfiguration: LoadSpacingConfiguration
}

/**
 * Component for selecting time on job site value
 * Displays a list of selectable time options matching Figma specifications
 * Title is handled by the step header
 */
export const TimeOnJobSiteSelector: React.FC<TimeOnJobSiteSelectorProps> = ({
  loadSpacingConfiguration
}) => {
  const {timeOnJobSite, setTimeOnJobSite} = useLoadSpacing()

  // Handle time selection
  const handleTimeSelect = useCallback(
    (time: number) => {
      setTimeOnJobSite(time)
    },
    [setTimeOnJobSite]
  )

  if (
    !loadSpacingConfiguration.loadSpacing.isEnabled ||
    !loadSpacingConfiguration.loadSpacing.timeOnJobSite
  ) {
    return null
  }

  const timeSettings = loadSpacingConfiguration.loadSpacing.timeOnJobSite

  // Get current value or default
  const currentValue = timeOnJobSite?.time ?? timeSettings?.default

  // Generate time options based on settings
  const timeOptions: number[] = []
  for (let time = timeSettings.min; time <= timeSettings.max; time += timeSettings.step) {
    timeOptions.push(time)
  }

  return (
    <Box sx={{width: '100%'}}>
      {/* Time Options List */}
      <Box sx={{width: '100%', overflow: 'auto'}}>
        <List sx={{padding: 0}} data-test-id="time-on-job-site-list">
          {timeOptions.map((time, index) => {
            const isSelected = time === currentValue
            const isLast = index === timeOptions.length - 1

            return (
              <ListItem
                key={time}
                sx={{
                  padding: 0,
                  borderBottom: !isLast ? '1px solid #e8ecf0' : 'none'
                }}
              >
                <ListItemButton
                  onClick={() => handleTimeSelect(time)}
                  sx={{
                    padding: '12px 16px',
                    backgroundColor: isSelected ? '#f2f8fd' : '#ffffff',
                    '&:hover': {
                      backgroundColor: '#f2f8fd'
                    },
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: '48px'
                  }}
                  data-test-id={`time-option-${time}`}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: '#29333d'
                    }}
                  >
                    {time} {timeSettings.uom}
                  </Typography>
                  {isSelected && (
                    <Check
                      sx={{
                        fontSize: '16px',
                        color: '#016ad4'
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Box>
    </Box>
  )
}
